import { useEffect, useState } from "react";
import { useAuthStore } from "@companion-core/shared/app/Store/authStore";
import { useMember, useVerifyAccount } from "@companion-core/shared/app/Queries";
import { useClientToken } from "@companion-core/shared/app/Hooks/clientToken";
import { setUser } from "@companion-core/shared/app/tracking";
import { APIMemberData } from "@companion-core/shared/app/Interfaces/API";

/**
 * Populate member store.
 * Fetch member data and subscriptions data only if user is authenticated.
 * Update store with data or reset if user is not authenticated.
 * @returns {boolean} isPopulatedMember
 */
export const usePopulateMember = (): {
  isPopulatedMember: boolean;
  memberData: APIMemberData;
} => {
  const [isPopulatedMember, setIsPopulatedMember] = useState<boolean>(false);
  const { authenticated } = useAuthStore((state) => ({ authenticated: state.authenticated }));
  const { data: memberData, isFetched: isMemberFetched } = useMember({
    withSubscriptions: true,
    enabled: authenticated,
  });

  useEffect(() => {
    if (authenticated && isMemberFetched) {
      setUser(memberData?.id ?? "", memberData?.subscriptions?.[0]?.id ?? "");
      setIsPopulatedMember(true);
    } else if (!authenticated) {
      setUser("", "");
    }
  }, [isMemberFetched, authenticated, memberData]);

  return {
    isPopulatedMember: !authenticated || isPopulatedMember,
    memberData,
  };
};

/**
 * Read the member data already fetched by `usePopulateMember` at the app root.
 * Serves the cached `["member"]` entry without refetching it, and stays subscribed
 * so invalidations are picked up. Falls back to a fetch if the cache is empty.
 *
 * The query is disabled while unauthenticated, so `isPending` stays true in that case:
 * pair it with `authenticated` before using it as a loading flag.
 * @returns {{ data: APIMemberData | undefined, isPending: boolean }}
 */
export const useMemberData = (): { data: APIMemberData | undefined; isPending: boolean } => {
  const { authenticated } = useAuthStore((state) => ({ authenticated: state.authenticated }));
  const { data, isPending } = useMember({
    withSubscriptions: true,
    enabled: authenticated,
    staleTime: Infinity,
  });
  return { data, isPending };
};

/**
 * Function to verify a member using a verification token.
 *
 * @param {string} vToken - The verification token.
 * @returns {Object} - An object containing the verified member data and a flag indicating if the verification is loading.
 */
export const useVerifyMember = (vToken: string) => {
  const fetchClientToken = useClientToken();
  const { accessTokenClient } = useAuthStore((state) => ({
    accessTokenClient: state.accessTokenClient,
  }));
  const { data, isPending } = useVerifyAccount(accessTokenClient ?? "", vToken, fetchClientToken);

  return { verifiedMemberData: data, isVerifyLoading: isPending };
};
